<template>
  <main
    role="main"
    class="col content-spacing"
    style="max-width: 88% !important"
  >
    <section>
      <PageHeader pageTitle="Leads qualificados" style="padding-top: 1rem;">
        <div style="display: flex; gap: 0.5rem">
          <QualifiedLeadsCitySelect v-model="municipioList" />
          <QualifiedLeadsPrimaryCnaeSelect
            :disabled="municipioList.length === 0"
            v-model="cnaeList"
            :selectedCities="municipioList"
          />
        </div>

        <div style="display: flex; gap: 0.5rem">
          <b-form-group label="Data inicial">
            <b-form-datepicker
              locale="pt-BR"
              aria-label="Escolha a data inicial"
              v-model="startDate"
              title="Selecione a data inicial"
              label-no-date-selected="Nenhuma data selecionada"
            />
          </b-form-group>

          <b-form-group label="Data final">
            <b-form-datepicker
              locale="pt-BR"
              aria-label="Escolha a data final"
              v-model="endDate"
              :min="startDate"
              title="Selecione a data final"
              label-no-date-selected="Nenhuma data selecionada"
            />
          </b-form-group>
          <ResponsibleUserSelect
            v-model="responsibleUser"
            label="Usuário responsável"
            inputId="responsible-user-select-input"
          />
        </div>

        <div style="display: flex; gap: 0.5rem">
          <LeadsExtractedSelect
            v-model="isExtracted"
            label="Leads extraídos?"
            inputId="leads-extracted-select-input"
          />
        </div>

        <BaseButtonComponent
          variant="primary-action"
          style="padding: 1px 12px;"
          @click="getLeads"
        >
          Buscar
        </BaseButtonComponent>
      </PageHeader>
    </section>
    <div display="flex" style="display: flex; justify-content: space-between;">
      <BaseButtonComponent
        size="sm"
        variant="primary-action"
        @click="openAddLeadDialog()"
        style="width: 200px; margin: 10px 0;"
      >
        Adicionar lead de CPF
      </BaseButtonComponent>
      <BaseButtonComponent
        size="sm"
        variant="primary-action"
        @click="leadsExtraction()"
        style="width: 200px; margin: 10px 0;"
      >
        Extrair leads
      </BaseButtonComponent>
    </div>

    <AddLeadModal ref="addLeadModalRef" @getAll="getLeads" />

    <b-skeleton-table
      v-if="isLoading"
      animation="wave"
      style="margin-top: 2rem"
      :rows="5"
      :columns="8"
      :table-props="{ bordered: true, striped: true }"
    />

    <QualifiedLeadsTable v-else :list="leads" :isLoading="isLoading" />
  </main>
</template>

<script>
import {
  QualifiedLeadsPrimaryCnaeSelect,
  QualifiedLeadsCitySelect,
  LeadsTable,
  QualifiedLeadsTable,
  LeadsExtractedSelect,
  ResponsibleUserSelect,
} from '../../modules/leads/components';
import { SelectYear } from '../../ui/selects';
import { BaseButtonComponent } from '../../ui/buttons';
import { PageHeader } from '../../ui/page-header';
import { Api } from '../../vue/api';
import AddLeadModal from '../../modules/leads/components/modals/add-lead-modal.vue';
const API = new Api();

export default {
  data() {
    return {
      isLoading: false,
      leads: [],
      cnaeList: [],
      municipioList: [],
      startDate: null,
      endDate: null,
      isExtracted: null,
      responsibleUser: '',
    };
  },
  components: {
    QualifiedLeadsCitySelect,
    PageHeader,
    QualifiedLeadsPrimaryCnaeSelect,
    SelectYear,
    BaseButtonComponent,
    LeadsTable,
    QualifiedLeadsTable,
    AddLeadModal,
    LeadsExtractedSelect,
    ResponsibleUserSelect,
  },
  mounted() {
    this.municipioList = [];
    this.cnaeList = [];
    this.getLeads();
  },
  methods: {
    showToast(message, title = 'Aviso', variant = 'info') {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      });
    },
    validateDates() {
      return (
        (!this.startDate && !this.endDate) ||
        (this.startDate && this.endDate && this.endDate >= this.startDate)
      );
    },
    openAddLeadDialog() {
      this.$refs.addLeadModalRef.openModal();
    },
    async getLeads() {
      this.leads = [];
      if (!this.validateDates()) {
        this.showToast(
          'Por favor, selecione um período válido de data',
          'Erro',
          'danger',
        );
        return;
      }

      this.isLoading = true;
      try {
        const { model, ok, errors } = await API.post('/execute', {
          path: '/lead-management/qualified-leads',
          method: 'get',
          data: {
            cnaeList: this.cnaeList,
            municipioList: this.municipioList,
            startDate: this.startDate,
            endDate: this.endDate,
            isExtracted: this.isExtracted,
            responsibleUser: this.responsibleUser,
          },
        });

        if (!ok) {
          this.showToast(errors.message, 'Erro', 'danger');
          return;
        }

        if (!model || !model.length) {
          this.showToast('Nenhum lead encontrado.', 'Aviso', 'warning');
          return;
        }

        this.leads = model.map(lead => ({
          id: lead.id,
          name: lead.name,
          primaryCNAECode: lead.primary_CNAE,
          primaryCNAEDescription: lead.primary_CNAE_description,
          state: lead.state,
          cityCode: lead.city,
          cityDescription: lead.city_description,
          socialNumber: lead.social_number,
          observation: lead.observation || '',
          creationDate: lead.created_at,
          qualificationType: lead.qualification_type,
          UCNumber: lead.uc_number || '',
          holderName: lead.holder_name || '',
          hasSCEEPower:
            lead.has_scee_power !== undefined ? lead.has_scee_power : false,
          hasLateBill:
            lead.has_late_bill !== undefined ? lead.has_late_bill : false,
          kwhTotalConsumption: lead.kwh_total_consumption || 0,
          numberOfActiveUCs: lead.number_of_active_ucs || 0,
          phonePCFA: lead.phone_pcfa || '',
          emailPCFA: lead.email_pcfa || '',
          isExtracted: lead.is_extracted || false,
          responsibleUserName: lead.responsible_user_name || '',
          imageLinks: lead.image_links || [],
        }));
      } catch (error) {
        this.showToast(`Erro ao buscar leads`, 'Erro', 'danger');
      } finally {
        this.isLoading = false;
      }
    },

    async leadsExtraction() {
      if (!this.leads.length) {
        this.showToast('Nenhum lead selecionado.', 'Aviso', 'warning');
        return;
      }
      this.isLoading = true;
      try {
        const { ok, errors } = await API.post('/execute', {
          path: '/lead-management/qualified-leads/extract',
          method: 'post',
          data: {
            leadsIds: this.leads.map(lead => lead.id),
          },
        });

        if (!ok) {
          this.showToast(errors.message, 'Erro', 'danger');
          return;
        }

        this.showToast('Leads extraídos com sucesso.', 'Sucesso', 'success');
      } catch (error) {
        this.showToast('Erro ao extrair leads', 'Erro', 'danger');
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
